/**
 * Job-name patterns as dispatch matches them: Python's `fnmatchcase`
 * (`definitions.match`), so the editor's "matches" preview agrees with
 * what a failed job will actually trigger. `*` is any text, `?` one
 * character, `[seq]` / `[!seq]` a character class; everything else is
 * literal and case-sensitive.
 */

function escapeRegExp(text: string): string {
  return text.replace(/[\\^$.*+?()[\]{}|/-]/g, '\\$&');
}

/** One character for use inside a RegExp class. */
function classChar(c: string): string {
  return /[\\^\]-]/.test(c) ? `\\${c}` : c;
}

/**
 * The class at `pattern[start]` (a `[`) as a RegExp fragment and its end,
 * or null if unclosed (then the `[` is literal). As Python's `fnmatch`: a
 * leading `!` negates, a leading `]` is literal, a `-` between two
 * characters is a range and a reversed range (`z-a`) is dropped, a `-` at
 * either end is literal. A class left empty matches nothing; negated, any
 * one character.
 */
function charClass(pattern: string, start: number): { source: string; end: number } | null {
  let i = start + 1;
  const negate = pattern[i] === '!';
  if (negate) i += 1;
  const bodyStart = i;
  if (pattern[i] === ']') i += 1;
  while (i < pattern.length && pattern[i] !== ']') i += 1;
  if (i >= pattern.length) return null;
  const body = pattern.slice(bodyStart, i);
  const items: string[] = [];
  for (let j = 0; j < body.length; ) {
    const c = body.charAt(j);
    const to = body.charAt(j + 2);
    if (body[j + 1] === '-' && j + 2 < body.length) {
      if (c <= to) items.push(`${classChar(c)}-${classChar(to)}`);
      j += 3;
    } else {
      items.push(classChar(c));
      j += 1;
    }
  }
  if (items.length === 0) return { source: negate ? '.' : '(?!)', end: i };
  return { source: `[${negate ? '^' : ''}${items.join('')}]`, end: i };
}

export function patternRegExp(pattern: string): RegExp {
  let source = '';
  for (let i = 0; i < pattern.length; i += 1) {
    const c = pattern.charAt(i);
    if (c === '*') source += '.*';
    else if (c === '?') source += '.';
    else if (c === '[') {
      const cls = charClass(pattern, i);
      if (cls === null) source += '\\[';
      else {
        source += cls.source;
        i = cls.end;
      }
    } else source += escapeRegExp(c);
  }
  return new RegExp(`^${source}$`, 's');
}

export function matchesJob(pattern: string, job: string): boolean {
  try {
    return patternRegExp(pattern).test(job);
  } catch {
    // Never reachable by construction; a preview must not take the editor down.
    return false;
  }
}

/** The pattern that matches exactly `job`: its `[`, `*` and `?` escaped as fnmatch reads them. */
export function jobPatternFor(job: string): string {
  return job.replace(/[[*?]/g, '[$&]');
}

/** `patterns` (the form's one-per-line text) as a list, blank lines dropped. */
export function patternList(patterns: string): string[] {
  return patterns.split('\n').map((p) => p.trim()).filter((p) => p !== '');
}
